import { z } from 'zod';
import {
  ruleApplyWritePolicySchema,
  billingHistoryStateSchema,
  whenClauseSchema,
  whenOperatorSchema,
} from '@billing/validation/rules';
export {
  billingHistoryStateSchema,
  whenClauseSchema,
  whenOperatorSchema,
} from '@billing/validation/rules';
import {
  ApplyField,
  AssertionField,
  AssertionOperator,
  RuleMode,
  RuleSeverity,
  ScopeMode,
  WhenLogic,
} from '@billing/validation/rules';

// Builder schemas allow incomplete input; submission validation remains separate.
export const ruleActionSchema = z.union([
  z.literal(RuleSeverity.Block),
  z.literal(RuleSeverity.Alert),
  z.literal(RuleMode.ApplyData),
  z.literal(RuleMode.TariffTransforms),
  z.literal('flagOnly'),
]);
export const ruleModeSelectionSchema = z.union([
  z.enum(RuleMode),
  z.literal('flagOnly'),
]);

export const assertionRowSchema = z.object({
  id: z.number().catch(1),
  field: z
    .enum(AssertionField)
    .catch(AssertionField.ClaimServiceLocationIndicator),
  operator: z.enum(AssertionOperator).catch(AssertionOperator.Equals),
  valuesText: z.string().catch(''),
  billingHistory: billingHistoryStateSchema.catch(
    billingHistoryStateSchema.parse({})
  ),
});

export const applyUpdateRowSchema = z.object({
  id: z.number().catch(1),
  // Saved rules can contain application targets beyond the selectable field catalog.
  field: z.string().catch(ApplyField.ServiceLocationIndicator),
  value: z.string().catch(''),
  writePolicy: ruleApplyWritePolicySchema.optional(),
});

const savedRowIDSchema = z.number().optional().catch(undefined);
export const savedWhenClauseSchema = whenClauseSchema
  .loose()
  .extend({
    id: savedRowIDSchema,
    whenOperator: whenOperatorSchema.optional().catch(undefined),
  })
  .transform((clause) => {
    const excludedTariff = clause.scopeMode === ScopeMode.TariffNotEqual;
    return {
      ...clause,
      scopeMode: excludedTariff ? ScopeMode.ExactTariff : clause.scopeMode,
      whenOperator: clause.whenOperator ?? (excludedTariff ? 'isNot' : 'is'),
    };
  });

const savedBillingWindowSchema = z.looseObject({
  type: billingHistoryStateSchema.shape.windowType,
  amount: z.number().positive().transform(String).catch('1'),
  unit: z.enum(['days', 'weeks', 'months']).catch('days'),
});
const defaultBillingWindow = savedBillingWindowSchema.parse({});
export const savedBillingHistorySchema = z.looseObject({
  enabled: z.boolean().catch(false),
  window: savedBillingWindowSchema.catch(defaultBillingWindow),
  sameProvider: z.unknown().transform((value) => value !== false),
});

export const savedAssertionRowSchema = assertionRowSchema
  .omit({ billingHistory: true })
  .loose()
  .extend({
    id: savedRowIDSchema,
    window: savedBillingWindowSchema.catch(defaultBillingWindow),
    sameProvider: z.boolean().catch(true),
  })
  .transform((check) => ({
    ...check,
    billingHistory: {
      windowType: check.window.type,
      amount: check.window.amount,
      unit: check.window.unit,
      sameProvider: check.sameProvider,
    },
  }));

export const savedApplyUpdateSchema = applyUpdateRowSchema.loose().extend({
  id: savedRowIDSchema,
  value: z
    .unknown()
    .transform((value) => (value === undefined ? '' : String(value))),
});
export const savedApplyPayloadSchema = z.looseObject({
  updates: z.array(z.unknown()).optional().catch(undefined),
});
export const savedEventParamsSchema = z.looseObject({});
export const savedRuleMetadataSchema = z.looseObject({
  when: z
    .looseObject({
      operator: z.enum(WhenLogic).catch(WhenLogic.All),
      clauses: z.array(z.unknown()).catch([]),
    })
    .optional()
    .catch(undefined),
  checks: z.array(z.unknown()).optional().catch(undefined),
  billingHistory: savedBillingHistorySchema.optional().catch(undefined),
  event: z
    .looseObject({
      params: savedEventParamsSchema.catch({}),
    })
    .optional()
    .catch(undefined),
});

export type AssertionRow = z.infer<typeof assertionRowSchema>;
export type ApplyUpdateRow = z.infer<typeof applyUpdateRowSchema>;
export type BillingHistoryState = z.infer<typeof billingHistoryStateSchema>;
export type WhenClause = z.infer<typeof whenClauseSchema>;
export type WhenOperator = z.infer<typeof whenOperatorSchema>;
export type RuleAction = z.infer<typeof ruleActionSchema>;
export type RuleModeSelection = z.infer<typeof ruleModeSelectionSchema>;
export type SavedRuleMetadata = z.infer<typeof savedRuleMetadataSchema>;
